// ============ SEARCHING ALGORITHMS ============

export const generateBinarySearchSteps = (array, target) => {
  const steps = [];
  let left = 0;
  let right = array.length - 1;

  steps.push({
    line: 3,
    description: `Initialize left=0, right=${right}`,
    variables: { left, right, target, arrayLength: array.length },
    left,
    right,
    mid: -1
  });

  while (left <= right) {
    const mid = left + Math.floor((right - left) / 2);

    steps.push({
      line: 6,
      description: `Calculate mid = ${left} + (${right} - ${left}) / 2 = ${mid}`,
      variables: { left, right, mid, midValue: array[mid], target },
      left,
      right,
      mid
    });

    steps.push({
      line: 8,
      description: `Check if array[${mid}] (${array[mid]}) equals target (${target})`,
      variables: { left, right, mid, midValue: array[mid], target },
      left,
      right,
      mid,
      comparison: array[mid] === target
    });

    if (array[mid] === target) {
      steps.push({
        line: 9,
        description: `Target found at index ${mid}`,
        variables: { result: mid },
        left,
        right,
        mid,
        found: true,
        result: mid
      });
      return steps;
    } else if (array[mid] < target) {
      steps.push({
        line: 11,
        description: `${array[mid]} < ${target}, search right half. left = ${mid + 1}`,
        variables: { left: mid + 1, right, target },
        left: mid + 1,
        right,
        mid
      });
      left = mid + 1;
    } else {
      steps.push({
        line: 13,
        description: `${array[mid]} > ${target}, search left half. right = ${mid - 1}`,
        variables: { left, right: mid - 1, target },
        left,
        right: mid - 1,
        mid
      });
      right = mid - 1;
    }
  }

  steps.push({
    line: 17,
    description: `Target ${target} not found in array`,
    variables: { result: -1 },
    left,
    right,
    mid: -1,
    found: false,
    result: -1
  });

  return steps;
};

export const generateSearchRotatedSteps = (array, target) => {
  const steps = [];
  let left = 0;
  let right = array.length - 1;

  steps.push({
    line: 3,
    description: `Initialize left=0, right=${right}`,
    variables: { left, right, target },
    left,
    right,
    mid: -1
  });

  while (left <= right) {
    const mid = left + Math.floor((right - left) / 2);

    steps.push({
      line: 6,
      description: `Calculate mid = ${mid}, array[mid] = ${array[mid]}`,
      variables: { left, right, mid, midValue: array[mid] },
      left,
      right,
      mid
    });

    if (array[mid] === target) {
      steps.push({
        line: 9,
        description: `Target ${target} found at index ${mid}`,
        variables: { result: mid },
        left,
        right,
        mid,
        found: true,
        result: mid
      });
      return steps;
    }

    if (array[left] <= array[mid]) {
      steps.push({
        line: 14,
        description: `Left half is sorted: [${array[left]}...${array[mid]}]`,
        variables: { left, mid, leftVal: array[left], midVal: array[mid] },
        left,
        right,
        mid
      });

      if (target >= array[left] && target < array[mid]) {
        steps.push({
          line: 15,
          description: `Target ${target} lies in sorted left half. right = ${mid - 1}`,
          variables: { left, right: mid - 1 },
          left,
          right: mid - 1,
          mid
        });
        right = mid - 1;
      } else {
        steps.push({
          line: 17,
          description: `Target ${target} not in left half. Search right. left = ${mid + 1}`,
          variables: { left: mid + 1, right },
          left: mid + 1,
          right,
          mid
        });
        left = mid + 1;
      }
    } else {
      steps.push({
        line: 21,
        description: `Right half is sorted: [${array[mid]}...${array[right]}]`,
        variables: { mid, right, midVal: array[mid], rightVal: array[right] },
        left,
        right,
        mid
      });

      if (target > array[mid] && target <= array[right]) {
        steps.push({
          line: 22,
          description: `Target ${target} lies in sorted right half. left = ${mid + 1}`,
          variables: { left: mid + 1, right },
          left: mid + 1,
          right,
          mid
        });
        left = mid + 1;
      } else {
        steps.push({
          line: 24,
          description: `Target ${target} not in right half. Search left. right = ${mid - 1}`,
          variables: { left, right: mid - 1 },
          left,
          right: mid - 1,
          mid
        });
        right = mid - 1;
      }
    }
  }

  steps.push({
    line: 30,
    description: `Target ${target} not found`,
    variables: { result: -1 },
    left,
    right,
    mid: -1,
    found: false,
    result: -1
  });

  return steps;
};

export const generateFindPeakSteps = (array) => {
  const steps = [];
  let left = 0;
  let right = array.length - 1;

  steps.push({
    line: 3,
    description: `Initialize left=0, right=${right}`,
    variables: { left, right },
    left,
    right,
    mid: -1
  });

  while (left < right) {
    const mid = left + Math.floor((right - left) / 2);

    steps.push({
      line: 6,
      description: `Calculate mid = ${mid}, comparing array[${mid}]=${array[mid]} with array[${mid + 1}]=${array[mid + 1]}`,
      variables: { left, right, mid, midValue: array[mid], nextValue: array[mid + 1] },
      left,
      right,
      mid
    });

    if (array[mid] < array[mid + 1]) {
      steps.push({
        line: 9,
        description: `${array[mid]} < ${array[mid + 1]}, peak on right. left = ${mid + 1}`,
        variables: { left: mid + 1, right },
        left: mid + 1,
        right,
        mid
      });
      left = mid + 1;
    } else {
      steps.push({
        line: 12,
        description: `${array[mid]} >= ${array[mid + 1]}, peak at mid or left. right = ${mid}`,
        variables: { left, right: mid },
        left,
        right: mid,
        mid
      });
      right = mid;
    }
  }

  steps.push({
    line: 16,
    description: `Peak found at index ${left} with value ${array[left]}`,
    variables: { result: left, peakValue: array[left] },
    left,
    right,
    mid: left,
    found: true,
    result: left
  });

  return steps;
};

// ============ SORTING ALGORITHMS ============

export const generateBubbleSortSteps = (array) => {
  const steps = [];
  const nums = [...array];
  const n = nums.length;
  let swapCount = 0;
  let comparisonCount = 0;

  steps.push({
    line: 2,
    description: `Start Bubble Sort. Array length: ${n}`,
    variables: { n },
    array: [...nums],
    pass: 0,
    swapCount,
    comparisonCount,
    sorted: []
  });

  for (let i = 0; i < n - 1; i++) {
    let swapped = false;
    const sortedIndices = [];
    for (let s = n - i; s < n; s++) sortedIndices.push(s);

    steps.push({
      line: 4,
      description: `Pass ${i + 1}: Bubble largest element to position ${n - i - 1}`,
      variables: { pass: i + 1, i },
      array: [...nums],
      pass: i + 1,
      swapCount,
      comparisonCount,
      sorted: sortedIndices
    });

    for (let j = 0; j < n - i - 1; j++) {
      comparisonCount++;
      steps.push({
        line: 7,
        description: `Compare nums[${j}]=${nums[j]} with nums[${j + 1}]=${nums[j + 1]}`,
        variables: { j, val1: nums[j], val2: nums[j + 1] },
        array: [...nums],
        pass: i + 1,
        comparing: [j, j + 1],
        swapCount,
        comparisonCount,
        sorted: sortedIndices
      });

      if (nums[j] > nums[j + 1]) {
        const temp = nums[j];
        nums[j] = nums[j + 1];
        nums[j + 1] = temp;
        swapped = true;
        swapCount++;

        steps.push({
          line: 9,
          description: `Swap: ${nums[j + 1]} > ${nums[j]}, exchange positions`,
          variables: { j, swap: true },
          array: [...nums],
          pass: i + 1,
          swapping: [j, j + 1],
          swapCount,
          comparisonCount,
          sorted: sortedIndices
        });
      }
    }

    if (!swapped) {
      steps.push({
        line: 18,
        description: `No swaps in this pass. Array is sorted. Exit early.`,
        variables: { swapped: false },
        array: [...nums],
        pass: i + 1,
        swapCount,
        comparisonCount,
        sorted: Array.from({ length: n }, (_, idx) => idx)
      });
      break;
    }
  }

  steps.push({
    line: 22,
    description: `Sorting complete. Total swaps: ${swapCount}, comparisons: ${comparisonCount}`,
    variables: { swapCount, comparisonCount },
    array: [...nums],
    pass: n - 1,
    swapCount,
    comparisonCount,
    sorted: Array.from({ length: n }, (_, idx) => idx),
    result: [...nums]
  });

  return steps;
};

export const generateQuickSortSteps = (array) => {
  const steps = [];
  const nums = [...array];
  let swapCount = 0;
  let comparisonCount = 0;
  const sortedIndices = new Set();

  const partition = (low, high, level) => {
    const pivot = nums[high];
    steps.push({
      line: 15,
      description: `Partition [${low}..${high}]. Pivot = nums[${high}] = ${pivot}`,
      variables: { low, high, pivot },
      array: [...nums],
      pivot,
      pivotIndex: high,
      subArray: [low, high],
      pass: level,
      swapCount,
      comparisonCount,
      sorted: Array.from(sortedIndices)
    });

    let i = low - 1;

    for (let j = low; j < high; j++) {
      comparisonCount++;
      steps.push({
        line: 19,
        description: `Compare nums[${j}]=${nums[j]} with pivot ${pivot}`,
        variables: { i, j, currentValue: nums[j], pivot },
        array: [...nums],
        pivot,
        pivotIndex: high,
        comparing: [j],
        subArray: [low, high],
        pass: level,
        swapCount,
        comparisonCount,
        sorted: Array.from(sortedIndices)
      });

      if (nums[j] <= pivot) {
        i++;
        if (i !== j) {
          const temp = nums[i];
          nums[i] = nums[j];
          nums[j] = temp;
          swapCount++;

          steps.push({
            line: 21,
            description: `nums[${j}](${nums[i]}) <= pivot. Swap with position ${i}`,
            variables: { i, j },
            array: [...nums],
            pivot,
            pivotIndex: high,
            swapping: [i, j],
            subArray: [low, high],
            pass: level,
            swapCount,
            comparisonCount,
            sorted: Array.from(sortedIndices)
          });
        }
      }
    }

    // Place pivot in correct position
    const temp = nums[i + 1];
    nums[i + 1] = nums[high];
    nums[high] = temp;
    if (i + 1 !== high) swapCount++;

    sortedIndices.add(i + 1);

    steps.push({
      line: 27,
      description: `Place pivot ${pivot} at correct position ${i + 1}`,
      variables: { pivotFinalIndex: i + 1 },
      array: [...nums],
      pivotIndex: i + 1,
      swapping: [i + 1, high],
      subArray: [low, high],
      pass: level,
      swapCount,
      comparisonCount,
      sorted: Array.from(sortedIndices)
    });

    return i + 1;
  };

  const quickSortHelper = (low, high, level = 0) => {
    if (low < high) {
      steps.push({
        line: 10,
        description: `Recursive call on subarray [${low}..${high}]`,
        variables: { low, high, level },
        array: [...nums],
        subArray: [low, high],
        pass: level,
        swapCount,
        comparisonCount,
        sorted: Array.from(sortedIndices)
      });

      const pi = partition(low, high, level);
      quickSortHelper(low, pi - 1, level + 1);
      quickSortHelper(pi + 1, high, level + 1);
    } else if (low === high) {
      sortedIndices.add(low);
    }
  };

  steps.push({
    line: 2,
    description: `Start Quick Sort on array of length ${nums.length}`,
    variables: { length: nums.length },
    array: [...nums],
    pass: 0,
    swapCount,
    comparisonCount,
    sorted: []
  });

  quickSortHelper(0, nums.length - 1);

  steps.push({
    line: 6,
    description: `Sorting complete. Swaps: ${swapCount}, Comparisons: ${comparisonCount}`,
    variables: { swapCount, comparisonCount },
    array: [...nums],
    pass: 0,
    swapCount,
    comparisonCount,
    sorted: Array.from({ length: nums.length }, (_, idx) => idx),
    result: [...nums]
  });

  return steps;
};

export const generateMergeSortSteps = (array) => {
  const steps = [];
  const nums = [...array];
  let comparisonCount = 0;

  const merge = (left, mid, right, level) => {
    const temp = new Array(right - left + 1);
    let i = left;
    let j = mid + 1;
    let k = 0;

    steps.push({
      line: 15,
      description: `Merge subarrays [${left}..${mid}] and [${mid + 1}..${right}]`,
      variables: { left, mid, right, level },
      array: [...nums],
      subArray: [left, right],
      pass: level,
      comparisonCount,
      sorted: []
    });

    while (i <= mid && j <= right) {
      comparisonCount++;
      steps.push({
        line: 19,
        description: `Compare nums[${i}]=${nums[i]} with nums[${j}]=${nums[j]}`,
        variables: { i, j, val1: nums[i], val2: nums[j] },
        array: [...nums],
        comparing: [i, j],
        subArray: [left, right],
        pass: level,
        comparisonCount,
        sorted: []
      });

      if (nums[i] <= nums[j]) {
        temp[k++] = nums[i++];
      } else {
        temp[k++] = nums[j++];
      }
    }

    while (i <= mid) temp[k++] = nums[i++];
    while (j <= right) temp[k++] = nums[j++];

    for (let m = 0; m < temp.length; m++) {
      nums[left + m] = temp[m];
    }

    steps.push({
      line: 30,
      description: `Merged into sorted subarray [${temp.join(', ')}]`,
      variables: { mergedRange: `[${left}..${right}]` },
      array: [...nums],
      subArray: [left, right],
      pass: level,
      comparisonCount,
      sorted: []
    });
  };

  const mergeSortHelper = (left, right, level = 0) => {
    if (left < right) {
      const mid = left + Math.floor((right - left) / 2);

      steps.push({
        line: 10,
        description: `Split [${left}..${right}] at mid=${mid}`,
        variables: { left, right, mid, level },
        array: [...nums],
        subArray: [left, right],
        pass: level,
        comparisonCount,
        sorted: []
      });

      mergeSortHelper(left, mid, level + 1);
      mergeSortHelper(mid + 1, right, level + 1);
      merge(left, mid, right, level);
    }
  };

  steps.push({
    line: 2,
    description: `Start Merge Sort on array of length ${nums.length}`,
    variables: { length: nums.length },
    array: [...nums],
    pass: 0,
    comparisonCount,
    sorted: []
  });

  mergeSortHelper(0, nums.length - 1);

  steps.push({
    line: 6,
    description: `Sorting complete. Comparisons: ${comparisonCount}`,
    variables: { comparisonCount },
    array: [...nums],
    pass: 0,
    comparisonCount,
    sorted: Array.from({ length: nums.length }, (_, idx) => idx),
    result: [...nums]
  });

  return steps;
};
